import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader2, Timer } from 'lucide-react';

import BreadcrumbTwo from '../../components/Breadcrumbs/BreadcrumbTwo';
import BasicPagination from '../../components/DataTable/BasicPagination';
import SectionWrapper from '../../components/SectionWrapper/SectionWrapper';
import SearchInputField from '../../components/Forms/Input/SearchInputField';
import SelectField from '../../components/Forms/Input/SelectField';
import ToggleButtonGroup, {
  ToggleOption,
} from '../../components/Forms/Buttons/ToggleButtonGroup';
import RestaurantTable from '../../features/restaurant/shared/RestaurantTable';
import WorkspaceLayout from '../../layout/WorkspaceLayout';

import useApiErrorHandler from '../../hooks/useApiErrorHandler';
import useAlerts from '../../hooks/useAlerts';
import useDebouncedValue from '../../hooks/useDebouncedValue';

import BranchService from '../../services/branch/BranchService';
import RestaurantMenuCategoryService from '../../services/restaurant/RestaurantMenuCategoryService';
import RestaurantMenuItemService from '../../services/restaurant/RestaurantMenuItemService';
import RestaurantSettingsService from '../../features/restaurant/settings/RestaurantSettingsService';
import { useAuthUser } from '../../hooks/auth/useAuthUser';

import { IBranchDropdown } from '../../types/branches/branchInterfaces';
import {
  RestaurantMenuCategoryDTO,
  RestaurantMenuItemDTO,
} from '../../types/restaurant/RestaurantMenuTypes';

type EstadoFilter = 'ALL' | 'ACTIVE' | 'INACTIVE';

const ESTADO_OPTIONS: ToggleOption<EstadoFilter>[] = [
  { value: 'ALL', label: 'Todos' },
  { value: 'ACTIVE', label: 'Activos' },
  { value: 'INACTIVE', label: 'Inactivos' },
];

const SORT = ['displayOrder,asc', 'displayName,asc', 'id,asc'];

const RestaurantMenuPrepTimes = () => {
  const { handle } = useApiErrorHandler();
  const { show } = useAlerts();
  const { hasRole } = useAuthUser();
  const canPickBranch =
    hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  const [branches, setBranches] = useState<IBranchDropdown[]>([]);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [loadingBranches, setLoadingBranches] = useState(true);

  const [timedMode, setTimedMode] = useState(false);
  const [globalMinutes, setGlobalMinutes] = useState(0);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const [categories, setCategories] = useState<RestaurantMenuCategoryDTO[]>([]);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [categoryId, setCategoryId] = useState(0);
  const [estado, setEstado] = useState<EstadoFilter>('ACTIVE');

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [rows, setRows] = useState<RestaurantMenuItemDTO[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);

  // Ediciones locales del minutaje por ítem + estado por fila.
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const [savedId, setSavedId] = useState<number | null>(null);
  const savedTimer = useRef<number | null>(null);

  useEffect(() => {
    const request = canPickBranch
      ? BranchService.fetchDropdown()
      : BranchService.fetchMyBranch().then((mine) => [mine]);
    request
      .then((items) => {
        setBranches(items);
        if (items.length) setBranchId(items[0].id);
      })
      .catch(handle)
      .finally(() => setLoadingBranches(false));
    return () => {
      if (savedTimer.current) window.clearTimeout(savedTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPickBranch]);

  useEffect(() => {
    if (!branchId) return;
    setSettingsLoaded(false);
    RestaurantMenuCategoryService.list(Number(branchId), true)
      .then((response) => setCategories(response.data ?? []))
      .catch(handle);
    RestaurantSettingsService.getByBranch(Number(branchId))
      .then((response) => {
        setTimedMode(
          response.data?.kitchen?.orderCompletionMode === 'TIMED'
        );
        setGlobalMinutes(response.data?.general?.defaultPreparationMinutes ?? 0);
      })
      .catch(handle)
      .finally(() => setSettingsLoaded(true));
    setCategoryId(0);
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, categoryId, estado, size]);

  useEffect(() => {
    if (!branchId || !timedMode) return;
    let cancelled = false;
    setLoading(true);
    RestaurantMenuItemService.listFiltered(Number(branchId), {
      page,
      size,
      sort: SORT,
      filters: {
        search: debouncedSearch.trim() || null,
        categoryId: categoryId || null,
        available: null,
        featured: null,
        active:
          estado === 'ALL' ? null : estado === 'ACTIVE' ? true : false,
      },
    })
      .then((data) => {
        if (cancelled) return;
        setRows(data.content);
        setTotalPages(data.page.totalPages);
        setTotalElements(data.page.totalElements);
        setDrafts(
          Object.fromEntries(
            data.content.map((item) => [
              item.id,
              item.preparationTimeMinutes && item.preparationTimeMinutes > 0
                ? String(item.preparationTimeMinutes)
                : '',
            ])
          )
        );
      })
      .catch(handle)
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, timedMode, page, size, debouncedSearch, categoryId, estado]);

  const categoryOptions = useMemo(
    () => [
      { value: 0, label: 'Todas las categorías' },
      ...categories.map((category) => ({
        value: category.id,
        label: category.name,
      })),
    ],
    [categories]
  );

  const dirty = (item: RestaurantMenuItemDTO) => {
    const current =
      item.preparationTimeMinutes && item.preparationTimeMinutes > 0
        ? String(item.preparationTimeMinutes)
        : '';
    return (drafts[item.id] ?? '') !== current;
  };

  const save = async (item: RestaurantMenuItemDTO) => {
    const raw = (drafts[item.id] ?? '').trim();
    const minutes = raw === '' ? null : Number(raw);
    if (minutes !== null && (Number.isNaN(minutes) || minutes < 0 || minutes > 1440)) {
      show('error', 'Tiempo inválido', 'Ingresa un valor entre 0 y 1440 minutos.');
      return;
    }
    setSavingId(item.id);
    try {
      const response = await RestaurantMenuItemService.updatePreparationTime(
        item.id,
        minutes
      );
      setRows((prev) =>
        prev.map((row) => (row.id === item.id ? response.data : row))
      );
      setSavedId(item.id);
      if (savedTimer.current) window.clearTimeout(savedTimer.current);
      savedTimer.current = window.setTimeout(() => setSavedId(null), 1500);
    } catch (error) {
      handle(error);
    } finally {
      setSavingId(null);
    }
  };

  const minutesCell = (item: RestaurantMenuItemDTO) => {
    const value = drafts[item.id] ?? '';
    const isSaving = savingId === item.id;
    const justSaved = savedId === item.id;

    return (
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={0}
          max={1440}
          value={value}
          disabled={isSaving}
          placeholder={globalMinutes ? `${globalMinutes} (global)` : '—'}
          onChange={(event) =>
            setDrafts((prev) => ({ ...prev, [item.id]: event.target.value }))
          }
          onKeyDown={(event) => {
            if (event.key === 'Enter') save(item);
          }}
          onBlur={() => dirty(item) && save(item)}
          className="w-24 rounded-lg border border-stroke bg-transparent px-3 py-1.5 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/10 dark:border-strokedark dark:text-white"
        />
        <span className="text-xs text-bodydark2">min</span>
        {isSaving ? (
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
        ) : justSaved ? (
          <Check className="h-4 w-4 text-success" />
        ) : dirty(item) ? (
          <button
            type="button"
            onClick={() => save(item)}
            className="rounded-md border border-primary/40 px-2 py-1 text-[11px] font-semibold text-primary transition hover:bg-primary/10"
          >
            Guardar
          </button>
        ) : null}
      </div>
    );
  };

  const columns = [
    {
      key: 'code',
      header: 'Código',
      className: 'w-28 font-mono text-xs text-bodydark2',
      cell: (row: RestaurantMenuItemDTO) => row.code,
    },
    {
      key: 'name',
      header: 'Ítem',
      cell: (row: RestaurantMenuItemDTO) => (
        <div>
          <p className="font-medium text-black dark:text-white">
            {row.displayName || row.productName}
          </p>
          {!row.active && (
            <span className="text-[11px] font-semibold text-bodydark2">
              Inactivo
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Categoría',
      className: 'text-sm text-bodydark2',
      cell: (row: RestaurantMenuItemDTO) => row.categoryName,
    },
    {
      key: 'station',
      header: 'Estación',
      className: 'text-sm text-bodydark2',
      cell: (row: RestaurantMenuItemDTO) =>
        row.preparationStationName || '—',
    },
    {
      key: 'minutes',
      header: 'Tiempo de preparación',
      className: 'w-64',
      cell: minutesCell,
    },
  ];

  const branchSelect = (
    <SelectField
      label="Sucursal"
      name="branch"
      value={branchId}
      iconKey="building"
      placeholder={loadingBranches ? 'Cargando…' : 'Sin sucursales'}
      options={branches.map((branch) => ({
        value: branch.id,
        label: branch.name,
      }))}
      onChange={(event) => setBranchId(Number(event.target.value))}
    />
  );

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />

      <SectionWrapper>
        <div className="overflow-hidden rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
          <div className="border-b border-stroke bg-white p-5 dark:border-strokedark dark:bg-boxdark sm:p-7">
            <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
              <div className="flex items-start gap-3">
                <span className="rounded-xl border border-primary/15 bg-primary/10 p-3 text-primary">
                  <Timer className="h-6 w-6" />
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                    Restaurante · Menú
                  </p>
                  <h1 className="mt-1 text-2xl font-bold text-black dark:text-white">
                    Tiempos de preparación
                  </h1>
                  <p className="mt-1 text-sm text-bodydark2 dark:text-bodydark">
                    Ajusta cuánto tarda cada ítem del menú directamente en la
                    tabla. Vacío = usa el tiempo predeterminado de la sucursal
                    {globalMinutes ? ` (${globalMinutes} min)` : ''}.
                  </p>
                </div>
              </div>

              {canPickBranch && (
                <div className="w-full xl:w-72">{branchSelect}</div>
              )}
            </div>
          </div>

          {!branchId ? null : !settingsLoaded ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-7 w-7 animate-spin text-primary" />
            </div>
          ) : !timedMode ? (
            <div className="p-8 text-center">
              <p className="mx-auto max-w-md rounded-xl border border-warning/30 bg-warning/10 px-4 py-6 text-sm text-black dark:text-white">
                Esta página solo aplica con el <strong>cierre de comanda “Por
                tiempo”</strong>. Actívalo en{' '}
                <Link
                  to="/restaurant/settings"
                  className="font-semibold text-primary underline"
                >
                  Configuración → Cocina
                </Link>{' '}
                para asignar el tiempo de cada ítem.
              </p>
            </div>
          ) : (
            <>
              <div className="grid gap-3 border-b border-stroke p-5 dark:border-strokedark sm:p-7 md:grid-cols-2 xl:grid-cols-[minmax(220px,1fr)_minmax(180px,240px)_auto] xl:items-end">
                <SearchInputField
                  label="Buscar"
                  name="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Nombre o código del ítem…"
                />
                <SelectField
                  label="Categoría"
                  name="category"
                  value={categoryId}
                  iconKey="tag"
                  options={categoryOptions}
                  onChange={(event) =>
                    setCategoryId(Number(event.target.value))
                  }
                />
                <ToggleButtonGroup
                  value={estado}
                  options={ESTADO_OPTIONS}
                  onChange={(value: EstadoFilter) => setEstado(value)}
                  className="mb-5"
                />
              </div>

              <div className="p-5 sm:p-7">
                {loading ? (
                  <div className="flex justify-center py-16">
                    <Loader2 className="h-7 w-7 animate-spin text-primary" />
                  </div>
                ) : rows.length === 0 ? (
                  <p className="py-16 text-center text-sm text-bodydark2">
                    No hay ítems de menú con esos filtros.
                  </p>
                ) : (
                  <>
                    <RestaurantTable
                      columns={columns}
                      rows={rows}
                      rowKey={(row) => row.id}
                      rowClassName={(row) => (row.active ? '' : 'opacity-60')}
                      minWidth="min-w-[820px]"
                    />

                    {/* Móvil */}
                    <div className="space-y-3 md:hidden">
                      {rows.map((row) => (
                        <div
                          key={row.id}
                          className="rounded-xl border border-stroke p-4 dark:border-strokedark"
                        >
                          <p className="font-medium text-black dark:text-white">
                            {row.displayName || row.productName}
                          </p>
                          <p className="mt-0.5 text-xs text-bodydark2">
                            {row.code} · {row.categoryName}
                          </p>
                          <div className="mt-3">{minutesCell(row)}</div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6">
                      <BasicPagination
                        currentPage={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        size={size}
                        onSizeChange={setSize}
                        totalElements={totalElements}
                      />
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </SectionWrapper>
    </WorkspaceLayout>
  );
};

export default RestaurantMenuPrepTimes;
